import {
  BodyTextProps,
  FlexRow,
  formatHash,
  HashLength,
} from '@make-software/cspr-ui';
import { BodyText, Tooltip } from '@block-explorer/ui';
import {
  ContractTypeName,
  contractTypes,
} from '../contract-type/contract-type-name';
import React from 'react';
import { DeployContractPackageResult } from '@block-explorer/entities';
import { HashFontSize } from '../table-data-account-info/table-data-account-info';
import { AvatarProps } from '../avatar/avatar';
import ContractIcon from '../contract-icon/contract-icon';
import { HistoryLink } from '../history-link/history-link';
import { buildRouterPath } from '../../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../../router/paths';
import { useTranslation } from 'react-i18next';
import { SHORTENED_NAME_LENGTH, truncateName } from '../action-messages/utils';

const mapScaleByHashFontSize: Record<HashFontSize, BodyTextProps['scale']> = {
  [HashFontSize.default]: 'xs',
  [HashFontSize.big]: 'sm',
};

export const UnknownContractInfo = ({
  hash,
  iconSize = 'small',
  hashFontSize = HashFontSize.big,
  hashLength,
}: {
  hash: string | null;
  iconSize?: AvatarProps['size'];
  hashFontSize?: HashFontSize;
  hashLength?: HashLength;
}) => {
  const itemsSpacing = iconSize === 'default' ? 12 : 4;
  if (!hash) {
    return null;
  }

  return (
    <FlexRow itemsSpacing={itemsSpacing} align={'center'}>
      <ContractIcon size={iconSize} contractTypeId={null} />
      <BodyText
        size={3}
        variation={'darkGray'}
        noWrap
        scale={mapScaleByHashFontSize[hashFontSize]}
        monotype
      >
        <HistoryLink
          href={buildRouterPath(CONTRACT_PACKAGE_PATH, { id: hash }, undefined)}
        >
          {formatHash(hash, hashLength)}
        </HistoryLink>
      </BodyText>
    </FlexRow>
  );
};

interface DefaultContractIdentifierProps {
  hash: string;
  contractPackage?: DeployContractPackageResult;
  hashLength?: HashLength;
  avatarSize?: AvatarProps['size'];
  hashFontSize?: HashFontSize;
  hideContractType?: boolean;
  loading?: boolean;
}

export const DefaultContractIdentifier = ({
  hash,
  contractPackage,
  hideContractType = false,
  hashLength = HashLength.TINY,
  avatarSize = 'tiny',
  hashFontSize = HashFontSize.default,
  loading,
}: DefaultContractIdentifierProps) => {
  const { t } = useTranslation();

  const { latest_version_contract_type_id, icon_url, name, contract_name } =
    contractPackage;

  const contractName = truncateName(
    name || contract_name,
    SHORTENED_NAME_LENGTH
  );

  const contractTypeName = latest_version_contract_type_id
    ? contractTypes[latest_version_contract_type_id]
    : '';
  const contractIdentifier = name ? contractName : formatHash(hash, hashLength);

  const showContractType =
    !hideContractType &&
    contractIdentifier.length + contractTypeName.length < SHORTENED_NAME_LENGTH;

  const itemsSpacing = avatarSize === 'default' ? 12 : 4;

  return (
    <FlexRow itemsSpacing={4} align={'center'}>
      <FlexRow itemsSpacing={itemsSpacing} align={'center'}>
        <ContractIcon
          size={avatarSize}
          contractTypeId={latest_version_contract_type_id}
          src={icon_url}
          loading={loading}
        />
        <Tooltip title={hash} caption={t('Contract Package Hash')} monotype>
          <BodyText
            size={3}
            variation={'darkGray'}
            noWrap
            scale={mapScaleByHashFontSize[hashFontSize]}
            monotype
          >
            <HistoryLink
              href={buildRouterPath(
                CONTRACT_PACKAGE_PATH,
                { id: hash },
                undefined
              )}
            >
              {contractIdentifier}
            </HistoryLink>
          </BodyText>
        </Tooltip>
      </FlexRow>
      {showContractType && (
        <BodyText
          size={3}
          variation={'darkGray'}
          noWrap
          scale={hashFontSize === HashFontSize.big ? 'sm' : 'xs'}
        >
          <ContractTypeName contractTypeId={latest_version_contract_type_id} />
        </BodyText>
      )}
    </FlexRow>
  );
};
